import { createRouter, createWebHistory } from "vue-router";
import routes from "@/router/routes";
import { socket } from "@/plugins/socket.js";
import bus from "@/bus";

import { useAuthStore } from "@/stores/auth";
import { useBalanceStore } from "@/stores/balance";
import { useNotifyStore } from "@/stores/notify";
import { useMessagesStore } from "@/stores/messages";

import NotFoundPage from "@/pages/NotFoundPage.vue";
import { useDialogsStore } from "@/stores/dialogs";
import { useUserPresenceStore } from "@/stores/userPresence";
import { useFeatureFlagsStore } from "@/stores/featureFlags";

import { fetchNotificationIndicators } from "@/api/repositories/user";
import { initializeApp } from "@/composables/useInitialize";

import { PROFILE_SWIPING_FLAG, REC_ENGINE_FLAG, PROFILE_DIRECTION_SWIPING_FLAG } from "@/constants";
import { IS_SWIPING_ALLOWED } from "@/utils/helpers";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: routes.concat([
    {
      path: "/:catchAll(.*)",
      name: "PageNotExist",
      component: NotFoundPage,
      meta: {
        public: true,
      },
    },
  ]),
  scrollBehavior(to) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: "smooth",
      };
    }
    return { top: 0 };
  },
});

const getFromName = (from) => {
  if (!from.name) {
    return "discovery";
  } else if (from.name === "profile.guest") {
    return { path: from.fullPath };
  } else {
    return from.name;
  }
};

const isProductionEnv = import.meta.env.VITE_APP_ENV === "prod";

const pickUtmParams = (query) => {
  const utmParams = {};
  const keys = ["utm_source", "utm_medium", "utm_campaign"];

  keys.forEach((key) => {
    if (query[key]) {
      utmParams[key] = query[key];
    }
  });

  return utmParams;
};

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  if (isProductionEnv && authStore.isAuthenticated) {
    authStore.logout();
  }

  const isPrivateRoute = to.matched.some((record) => record.meta.public === false);
  if (isProductionEnv && isPrivateRoute) {
    next({
      name: "landing",
      query: pickUtmParams(to.query),
    });
    return;
  }

  window.document.title = import.meta.env.VITE_APP_TITLE;
  to.meta.goBack = getFromName(from);

  // Save UTM parameters to local storage or Vuex store
  if (to.query.utm_source) {
    const { utm_source, utm_medium, utm_campaign } = to.query;
    localStorage.setItem("utm_source", utm_source);

    if (utm_medium) localStorage.setItem("utm_medium", utm_medium || "");
    if (utm_campaign) localStorage.setItem("utm_campaign", utm_campaign || "");

    // Fetch referral data if this is a referral link
    if (utm_source === "referral" && utm_medium) {
      const authStore = useAuthStore();
      // Only fetch if user is not authenticated and we don't already have referral data
      if (!authStore.isAuthenticated && !authStore.getReferralData()) {
        await authStore.fetchReferralData(utm_medium);
      }
    }
  }

  const onlyGuests = to.matched.some((record) => record.meta.onlyGuests);
  const isPublic = to.matched.some((record) => record.meta.public);

  const notifyStore = useNotifyStore();

  if (to.query.reset) {
    authStore.resetUser();
  }

  if (!isPublic && !authStore.isAuthenticated) {
    next({
      path: "/auth/login",
      query: { redirect: to.fullPath },
    });
    return;
  }

  if (authStore.isAuthenticated && !authStore.isUserInitialized()) {
    await authStore.updateUserFromApi();
  }

  // Initialize app data once per session after authentication
  if (authStore.isAuthenticated && authStore.isRegisterCompleted()) {
    await initializeApp();
  }

  const featureFlagsStore = useFeatureFlagsStore();
  if (authStore.isAuthenticated && authStore.isRegisterCompleted()) {
    const flagsToFetch = [REC_ENGINE_FLAG, PROFILE_DIRECTION_SWIPING_FLAG];

    const needsProfileSwipingFlag =
      IS_SWIPING_ALLOWED &&
      !featureFlagsStore.isFlagFetched(PROFILE_SWIPING_FLAG);

    if (needsProfileSwipingFlag) {
      flagsToFetch.push(PROFILE_SWIPING_FLAG);
    }

    const reloadQueued =
      localStorage.getItem("device_spec_sync_reload_queued") === "true";

    if (!reloadQueued && needsProfileSwipingFlag && flagsToFetch.length > 1) {
      try {
        localStorage.setItem("device_spec_sync_reload_queued", "true");
      } catch (_e) {
        /* ignore */
      }

      window.location.reload();
      next(false);
      return;
    }

    if (reloadQueued) {
      try {
        localStorage.removeItem("device_spec_sync_reload_queued");
      } catch (_e) {
        /* ignore */
      }
    }

    if (flagsToFetch.length > 0) {
      await featureFlagsStore.fetchFlags(flagsToFetch);
    }
  }

  const dialogsStore = useDialogsStore();
  if (
    authStore.isAuthenticated &&
    authStore.isRegisterCompleted() &&
    !dialogsStore.isDialogsListenerInitialized
  ) {
    socket()
      .private(`User.${authStore.getUser().id}`)
      .listen(".Dialogs", (payload) => {
        dialogsStore.updateDialog(payload.dialog);
      });
    dialogsStore.isDialogsListenerInitialized = true;
  }
  const messagesStore = useMessagesStore();
  if (
    authStore.isAuthenticated &&
    authStore.isRegisterCompleted() &&
    !messagesStore.handlerInitialized
  ) {
    socket()
      .private(`User.${authStore.getUser().id}`)
      .listen(".Message", (payload) => {
        messagesStore.setNewMessage(payload.message);
      });
    messagesStore.setHandlerInitialized();
  }

  const balanceStore = useBalanceStore();
  if (
    authStore.isAuthenticated &&
    authStore.isRegisterCompleted() &&
    !balanceStore.isBalanceInitialized
  ) {
    socket()
      .private(`User.${authStore.getUser().id}`)
      .listen(".WalletBalanceUpdated", (payload) => {
        const numberDiff = payload.wallet.credits - balanceStore.balance;
        const stringDiff = Intl.NumberFormat("en-US", {
          signDisplay: "exceptZero",
        }).format(numberDiff);
        let title =
          numberDiff > 0
            ? `You topped up ${Math.abs(numberDiff)} credits!`
            : `You spent ${Math.abs(numberDiff)} credits`;
        let message = `New balance: ${payload.wallet.credits}`;

        if (payload.custom_title) {
          title = payload.custom_title;
        }

        if (payload.custom_message) {
          message = payload.custom_message;
        }

        bus.global.emit("notification.top:show", {
          type: "success",
          title: title,
          message: message,
          credits: payload.custom_title ? undefined : stringDiff,
          duration: 5000,
        });
        balanceStore.setNewBalance(payload.wallet.credits);
      });
    balanceStore.setBalanceInitialized();
  }

  if (
    authStore.isAuthenticated &&
    authStore.isRegisterCompleted() &&
    !notifyStore.isNotificationsInitialized
  ) {
    const notificationIndicators = await fetchNotificationIndicators();
    notifyStore.setNotifications({
      new_messages_notifications: notificationIndicators.new_messages_notifications,
      new_likes_notifications: notificationIndicators.new_likes_notifications,
      new_matches_notifications: notificationIndicators.new_matches_notifications,
    });
    socket()
      .private(`User.${authStore.getUser().id}`)
      .listen(".NotificationsIndicators", (payload) => {
        const notifications = {
          new_messages_notifications: payload.new_messages_notifications,
          new_likes_notifications: payload.new_likes_notifications,
          new_matches_notifications: payload.new_matches_notifications,
        };
        notifyStore.setNotifications(notifications);
      });

    notifyStore.setNotificationsInitialized();
  }

  const userPresenceStore = useUserPresenceStore();
  if (
    authStore.isAuthenticated &&
    authStore.isRegisterCompleted() &&
    !userPresenceStore.isUserPresenceListenerInitialized
  ) {
    const presenceChannel = socket()
      .join("Users")
      .here((users) => {
        userPresenceStore.setOnlineUsersIds(users.map((user) => user.id));
      })
      .joining((user) => {
        if (user.id) userPresenceStore.addOnlineUserId(user.id);
      })
      .leaving((user) => {
        if (user.id) userPresenceStore.removeOnlineUserId(user.id);
      })
      .error((error) => {
        console.error(error);
      });
    // here((users) => {...}) is not always setting currently subscribed users into the store so we're trying to access those users directly via Pusher API: https://pusher.com/docs/channels/using_channels/presence-channels/#accessing-channel-members
    userPresenceStore.setOnlineUsersIds(
      Object.keys(presenceChannel.subscription.members.members)
    );
    userPresenceStore.isUserPresenceListenerInitialized = true;
  }

  if (authStore.isAuthenticated && !authStore.isRegisterCompleted()) {
    if (!to.name.includes("auth.signup")) {
      next({ name: "auth.signup" });
      return;
    }
    next();
    return;
  }

  if (
    authStore.isAuthenticated &&
    authStore.isRegisterCompleted() &&
    to.name.includes("auth")
  ) {
    next({ name: "discovery" });
    return;
  }

  if (onlyGuests && authStore.isAuthenticated) {
    if (to.name === "landing") {
      next({
        name: "discovery",
      });
      return;
    } else {
      next("");
      return;
    }
  }

  next();
});

// inspired by https://stackoverflow.com/a/77828857
let direction = "";
router.options.history.listen((_to, _from, info) => {
  direction = info.direction;
});

router.beforeEach((_to, from, next) => {
  const isBackNavigation = direction === "back";
  direction = "";

  if (
    isBackNavigation &&
    (from.name === "discovery" ||
      from.name === "pulse" ||
      from.name === "wholikedme" ||
      from.name === "dialogs")
  ) {
    next(false);
  } else {
    next();
  }
});

window.handleNativeBackPress = () => {
  const currentRoute = router.currentRoute.value;
  const topLevelPages = [
    "discovery",
    "pulse",
    "wholikedme",
    "dialogs",
    "profile",
  ];

  if (topLevelPages.includes(currentRoute.name)) {
    return false;
  }

  router.back();
  return true;
};

window.handleNativeRouteChange = (nativeRouteTrigger) => {
  try {
    const url = new URL(nativeRouteTrigger);
    if (url.pathname) router.push(url.pathname);
  } catch {
    /* Ignore */
  }
};

export default router;
